import { Link } from '@reach/router';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ButtonIconRound, Icon } from '../ui';
import { variables } from '../utils';
import { format } from 'date-fns';
import { NavigatorExtended } from '.';
import { func } from 'prop-types';

const TabButton = styled.button.attrs({
  className: 'pointer pb3'
})<{ active?: boolean }>`
  color: ${({ active }) => (active ? variables.navy : variables.blue)};
  border-bottom: 1px solid ${({ active }) => (active ? variables.navy : variables.lightGray)};
  &:hover {
    color: ${variables.navy};
  }
`;

const Spacer = () => <div className="w2 bb b--light-gray" />;

const TabButtonIcon = styled.button.attrs({
  className: 'pointer'
})`
  color: ${variables.blue};
  border-bottom: 1px solid ${variables.lightGray};
  &:hover svg {
    fill: ${variables.navy};
  }
`;

interface MenuSmall {
  activeTab: 'CAPTION' | 'DATA' | 'NOTE';
  setActiveTab: (value: 'CAPTION' | 'DATA' | 'NOTE') => void;
  note: {
    content?: string;
    context?: string;
    notedAt: Date;
    composedBy: {
      id: string;
      displayName: string;
    };
  };
  deleteNote(): void;
}

const MenuSmall: React.FC<MenuSmall> = ({ activeTab, setActiveTab, note, deleteNote }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  function copyLink() {
    (navigator as NavigatorExtended).clipboard!.writeText(window.location.href).then(() => {
      alert('Link copied to your clipboard!');
    });
  }

  return (
    <div className="sticky bg-white flex-none f6 pt2 ph3 mb3 w-100 mw6-ns center">
      <div className="flex">
        <div className="flex">
          <TabButton
            active={activeTab === 'DATA'}
            onClick={() => setActiveTab('DATA')}
            data-testid="data-tab"
          >
            Data
          </TabButton>
          <Spacer />

          {note.context && (
            <>
              <TabButton
                active={activeTab === 'CAPTION'}
                onClick={() => setActiveTab('CAPTION')}
                data-testid="caption-tab"
              >
                Caption
              </TabButton>
              <Spacer />
            </>
          )}

          {note.content && (
            <TabButton
              active={activeTab === 'NOTE'}
              onClick={() => setActiveTab('NOTE')}
              data-testid="note-tab"
            >
              Private Note
            </TabButton>
          )}
        </div>
        <div className="bb b--light-gray flex-auto" />
        <TabButtonIcon onClick={() => setMenuOpen(!menuOpen)} data-testid="menu-button">
          <Icon name="dots" className="pb2" />
        </TabButtonIcon>
      </div>
      {menuOpen && (
        <div>
          <div className="pv4 lh-copy bb b--light-gray">
            <div className="flex justify-between items-baseline">
              <span className="f6">{format(note.notedAt, 'MMM. DD, YYYY')}</span>
              <span className="f8 ">{format(note.notedAt, 'h:mm a')}</span>
            </div>
            <p className="f8 mv0 light-silver">by {note.composedBy.displayName}</p>
          </div>

          <div className="bb b--light-gray pv2">
            <Link to="./edit" className="flex items-center mr3" data-testid="edit-button">
              <ButtonIconRound name="edit" />
              <span className="f7 blue pl2">Edit</span>
            </Link>
          </div>

          <div className="bb b--light-gray pv2">
            <div className="flex items-center pointer">
              <ButtonIconRound name="trash" onClick={deleteNote} data-testid="delete-button" />
              <button className="f7 blue pl2" onClick={deleteNote}>
                Delete
              </button>
            </div>
          </div>

          <div className="bb b--light-gray pv2">
            <div className="flex items-center pointer">
              <ButtonIconRound name="link" onClick={copyLink} iconViewBox={16} />
              <button className="f7 pl2 blue pointer" onClick={copyLink}>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuSmall;
